// src/dashboard/pages/teacher/ToDoList/utils/toDoListDashboardUtils.js

export const STATUS_ORDER = ["Urgent", "En cours", "A faire", "Effectué"];

export const STATUS_META = {
  Urgent: {
    label: "Urgent",
    col: "bg-rose-50 dark:bg-rose-950/10",
    head: "text-rose-700 dark:text-rose-300",
    ring: "border-rose-200 dark:border-rose-500/20",
    badge: "bg-rose-600 text-white",
    accent: "bg-rose-600",
    soft: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
  },
  "En cours": {
    label: "En cours",
    col: "bg-sky-50 dark:bg-sky-950/10",
    head: "text-sky-700 dark:text-sky-300",
    ring: "border-sky-200 dark:border-sky-500/20",
    badge: "bg-sky-600 text-white",
    accent: "bg-sky-600",
    soft: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300",
  },
  "A faire": {
    label: "À faire",
    col: "bg-amber-50 dark:bg-amber-950/10",
    head: "text-amber-700 dark:text-amber-300",
    ring: "border-amber-200 dark:border-amber-500/20",
    badge: "bg-amber-500 text-slate-900",
    accent: "bg-amber-500",
    soft: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
  },
  Effectué: {
    label: "Effectué",
    col: "bg-emerald-50 dark:bg-emerald-950/10",
    head: "text-emerald-700 dark:text-emerald-300",
    ring: "border-emerald-200 dark:border-emerald-500/20",
    badge: "bg-emerald-600 text-white",
    accent: "bg-emerald-600",
    soft: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  },
};

const API_BASE = process.env.REACT_APP_API_URL || "";

export function isExternalUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (!value) return "";
  if (isExternalUrl(value)) return value;
  if (value.startsWith("data:") || value.startsWith("blob:")) return value;
  if (!value.startsWith("/uploads")) return value;

  const cleanBase = API_BASE.replace(/\/$/, "");
  return `${cleanBase}${value}`;
}

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function stripHtml(html = "") {
  return String(html || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isQuillEmpty(html) {
  if (!html) return true;

  const text = stripHtml(html);

  return html === "<p><br></p>" || text.length === 0;
}

export function getTaskUser(task) {
  return task?.user || task?.User || task?.utilisateur || {};
}

export function getUserDisplayName(user) {
  const nom = String(user?.nom || "").trim();
  const prenom = String(user?.prenom || "").trim();

  const fullName = `${nom.toUpperCase()} ${prenom}`.trim();

  return fullName || "Utilisateur non renseigné";
}

export function getUserAvatarSrc(user) {
  const photoUrl = user?.photoURL || user?.photo_url || user?.avatar || "";
  return toAbsoluteUrl(photoUrl) || "/img/user_avatar.jpg";
}

export function sortUsers(a, b) {
  return getUserDisplayName(a).localeCompare(getUserDisplayName(b), "fr", {
    sensitivity: "base",
  });
}

export function getCategoryOptions(tasks = []) {
  const categories = new Set();

  tasks.forEach((task) => {
    const category = String(task?.categorie || "").trim();
    if (category) categories.add(category);
  });

  return [...categories].sort((a, b) =>
    a.localeCompare(b, "fr", { sensitivity: "base" })
  );
}

export function filterTasks(tasks = [], filters = {}) {
  const query = normalizeString(filters.query);
  const status = String(filters.status || "");
  const userId = String(filters.userId || "");
  const category = String(filters.category || "");

  return tasks.filter((task) => {
    const user = getTaskUser(task);

    const searchable = normalizeString(
      [
        task?.titre,
        task?.categorie,
        stripHtml(task?.contenu),
        task?.statut,
        getUserDisplayName(user),
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchesQuery = !query || searchable.includes(query);
    const matchesStatus = !status || task?.statut === status;

    const currentUserId = task?.user_id || user?.id;
    const matchesUser = !userId || String(currentUserId) === userId;

    const matchesCategory =
      !category || String(task?.categorie || "") === category;

    return matchesQuery && matchesStatus && matchesUser && matchesCategory;
  });
}

export function groupTasksByStatus(tasks = []) {
  const map = new Map();

  STATUS_ORDER.forEach((status) => map.set(status, []));

  tasks.forEach((task) => {
    const status = STATUS_ORDER.includes(task?.statut)
      ? task.statut
      : "A faire";

    map.get(status).push(task);
  });

  return map;
}

export function calculateTaskStats(allTasks = [], filteredTasks = []) {
  const byStatus = {};

  STATUS_ORDER.forEach((status) => {
    byStatus[status] = 0;
  });

  let assignedUsersCount = new Set();
  let categoriesCount = new Set();

  allTasks.forEach((task) => {
    const status = STATUS_ORDER.includes(task?.statut)
      ? task.statut
      : "A faire";

    byStatus[status] = (byStatus[status] || 0) + 1;

    if (task?.user_id) assignedUsersCount.add(String(task.user_id));
    if (task?.categorie) categoriesCount.add(String(task.categorie));
  });

  const completionRate =
    allTasks.length > 0
      ? Math.round(((byStatus["Effectué"] || 0) / allTasks.length) * 100)
      : 0;

  return {
    total: allTasks.length,
    filtered: filteredTasks.length,
    urgent: byStatus.Urgent || 0,
    inProgress: byStatus["En cours"] || 0,
    todo: byStatus["A faire"] || 0,
    done: byStatus.Effectué || 0,
    completionRate,
    assignedUsersCount: assignedUsersCount.size,
    categoriesCount: categoriesCount.size,
    byStatus,
  };
}

export function buildTaskPayload(formData = {}) {
  return {
    categorie: String(formData.categorie || "").trim(),
    titre: String(formData.titre || "").trim(),
    contenu: formData.contenu || "",
    user_id: formData.user_id ? Number(formData.user_id) : null,
    statut: formData.statut || "A faire",
  };
}